import { BorderBox } from "@/components/atoms/BorderBox";
import { DynamicHeightScrollArea } from "@/components/atoms/DynamicHeightScrollArea";
import { HoverPopover } from "@/components/atoms/HoverPopover";
import { MarkSearchedPhrase } from "@/components/atoms/MarkSearchedPhrase";
import { TooltipButton } from "@/components/atoms/TooltipButton";
import DecisionModal from "@/components/custom/InfoPopups/DecisionModal";
import ErrorPopup from "@/components/custom/InfoPopups/ErrorPopup";
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";
import { DroppableContainer } from "@/components/molecules/DroppableContainer";
import { SearchAndFilterByCrmSystem } from "@/components/molecules/SearchAndFilterByCrmSystem";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DraggableCrmSystemItem } from "@/features/crm_system/components/molecules/DraggableCrmSystemItem";
import { crmRegistry } from "@/features/crm_system/lib/_core/crmRegistry";
import { CrmSystemIds } from "@/features/crm_system/lib/_core/crmSystem/crmSystem.types.gen";
import { parseAsCrmSystemId } from "@/features/crm_system/utils/nuqsParser";
import { useGetOrganizationCrmSystems } from "@/features/organization/hooks/utils/useGetOrganizationCrmSystems/useGetOrganizationCrmSystems";
import { ProjectRow } from "@/features/project/components/organisms/ProjectRow";
import { UseAllSurveyBankTemplatesReturnType, useGetAllSurveyBankTemplates } from "@/features/survey_bank/hooks/useQuery/useGetAllSurveyBankTemplates";
import { DuplicateSurveyTemplateDialog } from "@/features/surveys/components/organisms/DuplicateSurveyTemplateDialog";
import { SurveyCrmConnectionsDialog } from "@/features/surveys/components/organisms/SurveyCrmConnectionsDialog";
import { SurveyDetailsDialog } from "@/features/surveys/components/organisms/SurveyDetailsDialog";
import { CopySurveyToProjectParams, useCopySurveyToProject } from "@/features/surveys/hooks/useCopySurveyToProject";
import { DuplicateSurveyTemplateParams, useDuplicateSurveyTemplate } from "@/features/surveys/hooks/useDuplicateSurveyTemplate";
import { Tables } from "@/lib/supabase/supabase_types_override";
import { useProjectsList, UseProjectsListReturnData } from "@/lib/useQuery/project/useProjectsList";
import { useDeleteSurveyFromSurveyBank } from "@/features/survey_bank/hooks/useMutation/useDeleteSurveyFromSurveyBank";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { useQueryClient } from "@tanstack/react-query";
import { Cog, CopyPlus, Info, Loader, Pencil, Trash2, X } from "lucide-react";
import { parseAsArrayOf, parseAsString, useQueryState } from "nuqs";
import { useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { NavigationBarBreadCrumbs } from '@/components/molecules/BackNav';
import { useOrganizationStore } from '@/lib/zustand/useStore';
import { OrganizationTypeWordingResolver } from "@/features/organization/components/atoms/OrganizationTypeWordingResolver";
import { useDeleteSurvey } from "@/features/survey/hooks/useMutation/useDeleteSurvey";

export function ManageSurveyTemplates() {
    const { orgId } = useParams()

    /* 
    * Filters
    */
    const orgCrmSystems = useGetOrganizationCrmSystems();
    const [selectedCrmSystems, setSelectedCrmSystems] = useQueryState<CrmSystemIds[]>('crmSystems', parseAsArrayOf(parseAsCrmSystemId).withDefault(orgCrmSystems.map(system => system.id)));
    const [searchTerm, setSearchTerm] = useQueryState<string>('searchTerm', parseAsString.withDefault(''));
    const handleCheckboxChange = (id: CrmSystemIds, checked: boolean) => {
        setSelectedCrmSystems((prev) =>
            checked ? [...prev, id] : prev.filter((itemId) => itemId !== id)
        );
    };
    /* 
    * Fetches
    */
    const allSurveyBankTemplates = useGetAllSurveyBankTemplates({ whereCrmSystems: selectedCrmSystems, orgId: Number(orgId) });
    const allProjectsList = useProjectsList({ orgId: Number(orgId) });
    /* 
    * Filtering
    */
    const filteredProjects = allProjectsList.data?.filter(project => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();

        // Filter by project name
        if (project?.name?.toLowerCase().includes(searchLower)) return true;

        // Filter by project description
        if (project?.challenge?.toLowerCase().includes(searchLower)) return true;

        // Filter by linked CRM project name
        if (project.survey?.filter(s => s.name?.toLowerCase().includes(searchLower)).length) return true;

        return false;
    }) ?? [];

    const filteredSurveyBankTemplates = allSurveyBankTemplates.data?.filter(surveyBankTemplate => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();

        // Filter by CRM project name
        if (surveyBankTemplate.name.toLowerCase().includes(searchLower)) return true;

        // Filter by CRM project description
        if (surveyBankTemplate.description?.toLowerCase().includes(searchLower)) return true;

        return false;
    }) ?? [];
    /* 
    * Update Project
    */
    const [updatedProjectId, setUpdatedProjectId] = useState<Tables<'project'>['id'] | null>(null)
    const { mutateAsync: copySurveyToProject } = useCopySurveyToProject()
    const { mutateAsync: deleteSurvey, isPending: isDeletingSurvey } = useDeleteSurvey();
    const queryClient = useQueryClient();

    const removeCrmProjectConnection = (surveyId: Tables<'survey'>['id']) => {
        DecisionModal({
            title: "Are you sure?",
            message: "This action is irreversible"
        }).then(({ isConfirmed }) => {
            if (isConfirmed) {
                deleteSurvey(surveyId)
                    .finally(() => {
                        queryClient.invalidateQueries({ queryKey: ['project'], exact: false })
                    })
            }
        })
    }
    const handleDragEnd = (result: DropResult) => {
        const { source, destination, draggableId } = result;

        if (!destination) return;

        if (source.droppableId === 'crm-projects' && destination.droppableId.startsWith('project-')) {
            const projectId = destination.droppableId.replace('project-', '');
            const draggedSurveyTemplate = filteredSurveyBankTemplates.find(p => p.id.toString() === draggableId);
            const targetProject = filteredProjects.find(p => p.id.toString() === projectId);


            const requestBody: CopySurveyToProjectParams = {
                copyFromBank: true,
                project_id: projectId,
                description: draggedSurveyTemplate?.description,
                name: draggedSurveyTemplate?.name,
                id: draggedSurveyTemplate?.id
            }
            if (draggedSurveyTemplate && targetProject) {
                if (
                    targetProject?.crm_system !== draggedSurveyTemplate.crm_system
                ) {

                    DecisionModal({
                        title: "Are you sure?",
                        message: `This project crm system (${targetProject?.crm_system || 'None'}) is different than surveys template crm system (${draggedSurveyTemplate.crm_system}). Are you sure you want to proceed?`,
                        confirmButtonText: "Yes, proceed",
                        confirmButtonClass: "bg-primary hover:bg-primary/80 text-primary-foreground",
                    }).then((decision) => {
                        if (decision.isConfirmed) {
                            setUpdatedProjectId(targetProject.id)
                            copySurveyToProject({ ...requestBody, copyFromBank: true })
                                .then(() => {
                                    SuccessPopup("Survey added successfully")
                                })
                                .catch((error) => {
                                    ErrorPopup("Failed to add survey")
                                    console.error(error)
                                })
                                .finally(() => {
                                    setUpdatedProjectId(null)
                                })

                        }
                    })
                } else {
                    setUpdatedProjectId(targetProject.id)
                    copySurveyToProject({ ...requestBody, copyFromBank: true }).finally(() => {
                        setUpdatedProjectId(null)
                    });
                }
            }
        }
    };

    const { organization } = useOrganizationStore();

    const recordsByCrm = useMemo(() => {
        if(!allSurveyBankTemplates?.data || !orgCrmSystems) return;

        const crmCounts: Record<string, number> = {};
        for (const crm of orgCrmSystems) {
            crmCounts[crm.id] = 0;
        }

        for (const record of allSurveyBankTemplates?.data) {
            if (record.crm_system in crmCounts) {
                crmCounts[record.crm_system]++;
            }
        }

        return crmCounts;

    }, [allSurveyBankTemplates?.data]);

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <div className="flex flex-col flex-1 gap-2">
                <NavigationBarBreadCrumbs.Set
                    currentPageName={"Manage Survey Templates"}
                    pages={[
                        {
                            title: "Home",
                            goTo: `/welcome`
                        },
                        {
                            title: organization?.name || "-",
                            goTo: `/organization/${orgId}`
                        },

                    ]}
                />

                <SearchAndFilterByCrmSystem
                    selectedCrmSystems={selectedCrmSystems}
                    handleCheckboxChange={handleCheckboxChange}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    placeholder="Filter"
                    recordsByCrm={recordsByCrm}
                />
                <div className="grid flex-1 grid-cols-2 gap-2">
                    <div className="flex flex-col flex-1">
                        <Label>Survey templates</Label>
                        <BorderBox className="flex-1 mt-2 bg-white">
                            {(() => {
                                if (allSurveyBankTemplates.isLoading) {
                                    return (
                                        <div className="flex items-center justify-center w-full">
                                            <Loader className="animate-spin" />
                                        </div>
                                    )
                                }
                                if (allSurveyBankTemplates.isError) {
                                    return (
                                        <div className="flex items-center justify-center w-full">
                                            Error loading survey bank templates!
                                        </div>
                                    )
                                }
                                return (
                                    <DynamicHeightScrollArea offset={80}>
                                        <Droppable droppableId="crm-projects">
                                            {(provided) => (
                                                <div
                                                    className="flex flex-col gap-2"
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                >
                                                    {filteredSurveyBankTemplates.map((surveyTemplate, index) => (
                                                        <SurveyBankTemplateDraggableItemRow
                                                            key={surveyTemplate.id}
                                                            surveyTemplate={surveyTemplate}
                                                            index={index}
                                                        />
                                                    ))}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </DynamicHeightScrollArea>
                                )
                            })()}
                        </BorderBox>
                    </div>
                    <div className="flex flex-col flex-1">
                        <Label><OrganizationTypeWordingResolver capitalize plural /></Label>
                        <BorderBox className="flex-1 mt-2 bg-white">
                            {(() => {
                                if (allProjectsList.isLoading) {
                                    return (
                                        <div className="flex items-center justify-center w-full">
                                            <Loader className="animate-spin" />
                                        </div>
                                    )
                                }
                                if (allProjectsList.isError) {
                                    return (
                                        <div className="flex items-center justify-center w-full">
                                            Error loading projects!
                                        </div>
                                    )
                                }
                                return (
                                    <DynamicHeightScrollArea offset={80}>
                                        <div className="flex flex-col gap-2">
                                            {filteredProjects.map((project) => (
                                                <ProjectRowDroppableContainer
                                                    key={project.id}
                                                    project={project}
                                                    updatedProjectId={updatedProjectId}
                                                    removeCrmProjectConnection={removeCrmProjectConnection}
                                                    isDeletingSurvey={isDeletingSurvey}
                                                />
                                            ))}
                                        </div>
                                    </DynamicHeightScrollArea>
                                )
                            })()}
                        </BorderBox>
                    </div>
                </div>
                <div className="flex justify-end mt-3 space-x-2">
                    <Button asChild size='sm'>
                        <Link to={`/organization/${orgId}/manage-organization/survey-bank/add-template`}>
                            Add Template
                        </Link>
                    </Button>
                </div>
            </div>
        </DragDropContext>
    )
}


interface ProjectRowDroppableContainerProps {
    project: UseProjectsListReturnData[number];
    updatedProjectId: Tables<'project'>['id'] | null;
    removeCrmProjectConnection: (surveyId: Tables<'survey'>['id']) => void;
    isDeletingSurvey: boolean;
}

function ProjectRowDroppableContainer({ project, updatedProjectId, removeCrmProjectConnection, isDeletingSurvey }: ProjectRowDroppableContainerProps) {
    const [searchTerm] = useQueryState('searchTerm', parseAsString.withDefault(''));
    const [isConnectionsDialogOpen, setIsConnectionsDialogOpen] = useState(false);

    return (
        <>
            <SurveyCrmConnectionsDialog
                open={isConnectionsDialogOpen}
                onOpenChange={setIsConnectionsDialogOpen}
                project={project}
            />
            <ProjectRow
                key={project.id}
                project={project}
                crmSystem={project?.crm_system}
                searchTerm={searchTerm}
                isLoading={((updatedProjectId === project.id) || (isDeletingSurvey))}
                additionalActions={() => (
                    <TooltipButton
                        size='action'
                        tooltipContent="Survey CRM Connections"
                        onClick={() => setIsConnectionsDialogOpen(true)}
                    >
                        <Cog size={16} />
                    </TooltipButton>
                )}
            >
                <DroppableContainer
                    droppableId={`project-${project.id}`}
                    placeholder="Drop survey template here to add it to the project"
                >
                    {project.survey?.length ? (
                        <div className="flex flex-wrap gap-2">
                            {project.survey.map((survey) => (
                                <BorderBox
                                    key={survey.id}
                                    className="flex items-center w-auto gap-2 border-l-4"
                                    style={{ borderLeftColor: crmRegistry.getSystemById(project?.crm_system)?.branding.color }}
                                    size='xs'
                                >
                                    <MarkSearchedPhrase
                                        text={survey.name}
                                        className="font-medium text-wrap"
                                        searchTerm={searchTerm}
                                    />

                                    <TooltipButton
                                        size='action-xs'
                                        variant='destructive'
                                        tooltipContent="Delete"
                                        onClick={() => removeCrmProjectConnection(survey.id)}
                                    >
                                        <X size={12} />
                                    </TooltipButton>
                                </BorderBox>
                            ))}
                        </div>
                    ) : null}
                </DroppableContainer>
            </ProjectRow>
        </>
    )
}

interface SurveyBankTemplateDraggableItemRow {
    surveyTemplate: UseAllSurveyBankTemplatesReturnType[number];
    index: number;
}

function SurveyBankTemplateDraggableItemRow({ surveyTemplate, index }: SurveyBankTemplateDraggableItemRow) {
    const { orgId } = useParams()
    /* 
    * State
    */
    const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
    const [openDuplicateSurveyTemplateDialog, setOpenDuplicateSurveyTemplateDialog] = useState(false);
    const [searchTerm] = useQueryState('searchTerm', parseAsString.withDefault(''))
    /* 
    * Mutations
    */
    const { mutateAsync: duplicateSurveyTemplate, isPending } = useDuplicateSurveyTemplate();

    const onDuplicateSurveyHandler = async (params: DuplicateSurveyTemplateParams) => {
        try {
            await duplicateSurveyTemplate({
                surveyTemplate,
                params
            });
            SuccessPopup("Survey template duplicated successfully!");
            setOpenDuplicateSurveyTemplateDialog(false);
        } catch (error) {
            if (error instanceof Error) {
                console.error(error);
                ErrorPopup(error.message);
            } else {
                ErrorPopup("An unexpected error occurred while duplicating the survey template.");
            }
        }
    };
    const { mutateAsync: deleteSurveyTemplate } = useDeleteSurveyFromSurveyBank();
    const deleteSurveyTemplateHandler = () => {
        DecisionModal({
            title: "Are you sure?",
            message: "This action cannot be undone.",
        }).then((decision) => {
            if (decision.isConfirmed) {
                deleteSurveyTemplate(surveyTemplate.id)
                    .then(() => {
                        SuccessPopup("Survey template deleted successfully!");
                    })
                    .catch((error) => {
                        console.error(error);
                        ErrorPopup("Failed to delete survey template");
                    })
            }
        });
    }

    return (
        <>
            <SurveyDetailsDialog
                open={openDetailsDialog}
                onOpenChange={setOpenDetailsDialog}
                survey={surveyTemplate}
            />

            <DuplicateSurveyTemplateDialog
                show={openDuplicateSurveyTemplateDialog}
                onHide={() => setOpenDuplicateSurveyTemplateDialog(false)}
                name={surveyTemplate?.name}
                description={surveyTemplate?.description || ""}
                isPending={isPending}
                onSubmit={onDuplicateSurveyHandler}
            />
            <Draggable
                draggableId={surveyTemplate.id.toString()}
                index={index}
            >
                {(provided, snapshot) => (
                    <DraggableCrmSystemItem
                        provided={provided}
                        isDragging={snapshot.isDragging}
                        crmSystem={surveyTemplate.crm_system}
                    >
                        <div className="flex flex-col gap-0.5 flex-grow">
                            <MarkSearchedPhrase
                                text={surveyTemplate.name}
                                searchTerm={searchTerm}
                                // className="flex-1 min-w-0 font-medium line-clamp-1"
                                className="font-medium"
                            />

                            <HoverPopover
                                onHover
                                content={
                                    <MarkSearchedPhrase
                                        text={surveyTemplate.description || ''}
                                        searchTerm={searchTerm}
                                    />
                                }
                            >
                                <MarkSearchedPhrase
                                    text={surveyTemplate.description || ''}
                                    searchTerm={searchTerm}
                                    className="flex-1 min-w-0 text-start line-clamp-1 text-muted-foreground"
                                />
                            </HoverPopover>
                        </div>

                        <div className="flex flex-wrap justify-end gap-1 ml-auto lg:flex-nowrap">
                            <div className="flex gap-1">
                                <TooltipButton
                                    size='action'
                                    variant="success"
                                    onClick={(e) => { e.preventDefault(); setOpenDetailsDialog(true); }}
                                    tooltipContent="Show template details"

                                >
                                    <Info size={16} />
                                </TooltipButton>
                                <TooltipButton
                                    size='action'
                                    variant="secondary"
                                    onClick={(e) => { e.stopPropagation(); setOpenDuplicateSurveyTemplateDialog(true); }}
                                    tooltipContent="Duplicate template"
                                    disabled={isPending}
                                >
                                    {(isPending) ? (<Loader size="16" className={`animate-spin`} />) : (<CopyPlus size={16} />)}
                                </TooltipButton>
                            </div>
                            <div className="flex gap-1">
                                <TooltipButton
                                    size='action'
                                    tooltipContent="Edit template"
                                    asChild
                                >
                                    <Link to={`/organization/${orgId}/manage-organization/survey-bank/edit-template/${surveyTemplate.id}`}>
                                        <Pencil size={16} />
                                    </Link>
                                </TooltipButton>

                                <TooltipButton
                                    size='action'
                                    variant="destructive"
                                    onClick={deleteSurveyTemplateHandler}
                                    tooltipContent="Delete"
                                >
                                    <Trash2 size={16} />
                                </TooltipButton>
                            </div>
                        </div>
                    </DraggableCrmSystemItem>
                )}
            </Draggable>
        </>
    )
}